import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import companyService from '../../services/companyService';
import NotificationToast from '../../components/ui/NotificationToast';

const CompanyInfo = () => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', website: '', industry: '', address: '', city: '', state: '', pincode: '',
    description: '', logo_url: '', tax_id: '', founded: '', linkedin_url: '', twitter_url: '', facebook_url: ''
  });

  useEffect(() => {
    loadCompany();
  }, []);

  const loadCompany = async () => {
    try {
      setLoading(true);
      const result = await companyService.getCompanyInfo();
      if (result?.success) {
        const data = result.data || {};
        setCompany(data);
        setFormData({
          name: data.name || '', email: data.email || '', phone: data.phone || '', website: data.website || '',
          industry: data.industry || '', address: data.address || '', city: data.city || '', state: data.state || '',
          pincode: data.pincode || '', description: data.description || '', logo_url: data.logo_url || '',
          tax_id: data.tax_id || '', founded: data.founded || '', linkedin_url: data.linkedin_url || '',
          twitter_url: data.twitter_url || '', facebook_url: data.facebook_url || ''
        });
      }
    } catch (error) {
      console.error('Error loading company:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await companyService.updateCompanyInfo(formData);
      if (result?.success) {
        setNotification({ message: 'Company information updated successfully', type: 'success' });
        setIsEditing(false);
        loadCompany();
      } else {
        setNotification({ message: result?.error || 'Failed to update', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setNotification({ message: 'Invalid file type. Please upload JPEG, PNG, GIF, or WebP image', type: 'error' });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setNotification({ message: 'File size exceeds 5MB limit', type: 'error' });
      return;
    }

    try {
      setUploadingLogo(true);
      const result = await companyService.uploadLogo(file);
      if (result?.success) {
        setNotification({ message: 'Logo uploaded successfully', type: 'success' });
        setFormData({ ...formData, logo_url: result.data.logo_url });
        loadCompany();
      } else {
        setNotification({ message: result?.error || 'Failed to upload logo', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'Error uploading logo', type: 'error' });
    } finally {
      setUploadingLogo(false);
      e.target.value = ''; // Reset file input
    }
  };

  const industries = ['Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing', 'Retail', 'Consulting', 'Real Estate', 'Marketing', 'Legal Services', 'Construction', 'Transportation', 'Hospitality', 'Media', 'Other'];

  return (
    <div className="p-6">
      <NotificationToast message={notification?.message} type={notification?.type} onDismiss={() => setNotification(null)} />
      
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Company Information</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage your company profile and details</p>
        </div>
        {!isEditing && (
          <Button onClick={() => setIsEditing(true)}>
            <Icon name="Edit" size={16} className="mr-2" />
            Edit Information
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" /><p className="text-muted-foreground">Loading...</p></div>
      ) : isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-6 bg-background border border-border rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Company Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
            <Input label="Email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
            <Input label="Phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
            <Input label="Website" value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} />
            <div>
              <label className="block text-sm font-medium mb-2">Industry</label>
              <select value={formData.industry} onChange={(e) => setFormData({...formData, industry: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md">
                <option value="">Select Industry</option>
                {industries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
              </select>
            </div>
            <Input label="Tax ID / GST" value={formData.tax_id} onChange={(e) => setFormData({...formData, tax_id: e.target.value})} />
            <Input label="Founded Year" value={formData.founded} onChange={(e) => setFormData({...formData, founded: e.target.value})} />
          </div>
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-4">Company Logo</h3>
            <div className="flex items-start gap-6">
              {formData.logo_url && (
                <div className="flex-shrink-0">
                  <img 
                    src={`${import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || `http://${window.location.hostname}:8000`}${formData.logo_url}`} 
                    alt="Company Logo" 
                    className="w-32 h-32 object-contain border border-border rounded-lg p-2 bg-muted"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div className="flex-1">
                <label className="block text-sm font-medium mb-2">Upload Logo</label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`px-4 py-2 border border-border rounded-md cursor-pointer hover:bg-muted transition-colors ${
                      uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Icon name={uploadingLogo ? "Loader" : "Upload"} size={16} className="mr-2 inline" />
                    {uploadingLogo ? 'Uploading...' : 'Choose File'}
                  </label>
                  {formData.logo_url && (
                    <span className="text-sm text-muted-foreground">
                      Current logo will be replaced
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Supported formats: JPEG, PNG, GIF, WebP (Max 5MB)
                </p>
                <Input 
                  label="Or Enter Logo URL" 
                  value={formData.logo_url} 
                  onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                  className="mt-3"
                  placeholder="https://example.com/logo.png"
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          </div>
          <textarea value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md" rows="2" placeholder="Address" />
          <div className="grid grid-cols-3 gap-4">
            <Input label="City" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} />
            <Input label="State" value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} />
            <Input label="PIN Code" value={formData.pincode} onChange={(e) => setFormData({...formData, pincode: e.target.value})} />
          </div>
          <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md" rows="4" placeholder="Company Description" />
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-4">Social Media</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input label="LinkedIn URL" value={formData.linkedin_url} onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})} />
              <Input label="Twitter URL" value={formData.twitter_url} onChange={(e) => setFormData({...formData, twitter_url: e.target.value})} />
              <Input label="Facebook URL" value={formData.facebook_url} onChange={(e) => setFormData({...formData, facebook_url: e.target.value})} />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
            <Button type="submit">Save Changes</Button>
          </div>
        </form>
      ) : (
        <div className="bg-background border border-border rounded-lg p-6">
          {company && company.name ? (
            <div className="space-y-6">
              {company.logo_url && (
                <div className="flex justify-center">
                  <img 
                    src={`${import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || `http://${window.location.hostname}:8000`}${company.logo_url}`} 
                    alt="Company Logo" 
                    className="max-w-xs max-h-32 object-contain border border-border rounded-lg p-4 bg-muted"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Company Details</h3>
                <dl className="space-y-3">
                  <div><dt className="text-sm font-medium text-muted-foreground">Company Name</dt><dd className="text-sm text-foreground">{company.name}</dd></div>
                  {company.industry && <div><dt className="text-sm font-medium text-muted-foreground">Industry</dt><dd className="text-sm text-foreground">{company.industry}</dd></div>}
                  {company.description && <div><dt className="text-sm font-medium text-muted-foreground">Description</dt><dd className="text-sm text-foreground">{company.description}</dd></div>}
                  {company.tax_id && <div><dt className="text-sm font-medium text-muted-foreground">Tax ID / GST</dt><dd className="text-sm text-foreground">{company.tax_id}</dd></div>}
                  {company.founded && <div><dt className="text-sm font-medium text-muted-foreground">Founded</dt><dd className="text-sm text-foreground">{company.founded}</dd></div>}
                </dl>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                <dl className="space-y-3">
                  {company.email && <div><dt className="text-sm font-medium text-muted-foreground">Email</dt><dd className="text-sm text-foreground">{company.email}</dd></div>}
                  {company.phone && <div><dt className="text-sm font-medium text-muted-foreground">Phone</dt><dd className="text-sm text-foreground">{company.phone}</dd></div>}
                  {company.website && <div><dt className="text-sm font-medium text-muted-foreground">Website</dt><dd className="text-sm text-foreground"><a href={company.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{company.website}</a></dd></div>}
                  {company.address && <div><dt className="text-sm font-medium text-muted-foreground">Address</dt><dd className="text-sm text-foreground">{[company.address, company.city, company.state, company.pincode].filter(Boolean).join(', ')}</dd></div>}
                </dl>
              </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="Building" size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground mb-4">No company information set up yet</p>
              <Button onClick={() => setIsEditing(true)}>Set Up Company</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanyInfo;

